/**
 * Analytics Setup for GWS-Flows.
 *
 * Uses the "preload script" pattern (like cms-gcorp).
 * 1. Set configuration on gustoAC BEFORE importing the library.
 * 2. Set up a stub GustoAnalytics that queues calls until library loads.
 * 3. Dynamically import the library which auto-initializes and drains the queue.
 *
 * Key difference from zenpayroll: We use statelessMode for iframe compatibility.
 */

import { initAnalytics } from './initAnalytics';

// Import types (includes global Window declaration)
import './types';

// Stub type for the pre-initialization queue state
// Before the library loads, GustoAnalytics is an array that queues method calls
interface GustoAnalyticsStub {
  [key: string]: unknown;
  factory?: (method: string) => (...args: unknown[]) => GustoAnalyticsStub;
  initialize?: boolean;
  invoked?: boolean;
  methods?: string[];
  push: (item: unknown) => number;
  length: number;
}

// Get analytics config from window.ANALYTICS_CONFIG (set by Rails in head, before this JS runs)
const analyticsConfig =
  (
    window as Window & {
      ANALYTICS_CONFIG?: {
        environment?: string;
        analyticsEnabled?: boolean;
        snowplowTrackingUrl?: string;
        snowplowTrackerName?: string;
        snowplowAppId?: string;
      };
    }
  ).ANALYTICS_CONFIG || {};
const environment = analyticsConfig.environment || 'production';
const analyticsEnabled = analyticsConfig.analyticsEnabled === true;
const snowplowTrackingUrl = analyticsConfig.snowplowTrackingUrl || '';
const snowplowTrackerName = analyticsConfig.snowplowTrackerName || 'gws-flows';
const snowplowAppId = analyticsConfig.snowplowAppId || 'gws-flows';

// STEP 1: Set configuration BEFORE loading the library
// The @gusto/gusto-analytics package reads from gustoAC on init
window.gustoAC = {
  // STATELESS MODE - key for gws-flows (iframe-friendly, no cookies)
  statelessMode: true,

  environment,

  // Snowplow configuration - this is Gusto's core analytics pipeline
  // Events flow: Browser → Snowplow → Server pipeline → Amplitude
  snowplowEnabled: analyticsEnabled && snowplowTrackingUrl.length > 0,
  snowplowTrackingUrl,
  snowplowTrackerName,
  snowplowAppId,
  snowplowEmitterBufferSize: 1, // Send events immediately

  // Server-side routing - tells Snowplow to forward events to Amplitude
  // This is THE configuration that sends events to Amplitude
  serverSideEligibleProviders: ['amplitude'],

  // Consent override for B2B embedded context (implemented with Legal/Privacy approval).
  // In iframe environments, we cannot read the OptanonConsent cookie from the parent.
  // This provides consent data directly in the analytics payload.
  // Strictly necessary cookies are always enabled.
  consentOverride: {
    performance: true,
    functional: true,
    targeting: true,
  },

  // Control automatic behaviors - we handle page/click tracking ourselves
  autoInit: true, // Let the library auto-initialize
  autoPageEvent: false, // We control page view tracking via initAnalytics()
  autoIdentify: false,
};

// STEP 2: Create a stub GustoAnalytics that QUEUES method calls until library loads
// This allows code to call GustoAnalytics.track() immediately without waiting
const GustoAnalyticsObj: GustoAnalyticsStub = (window.GustoAnalytics = window.GustoAnalytics || []);

if (!GustoAnalyticsObj.initialize) {
  if (GustoAnalyticsObj.invoked) {
    console.error('[GWS-Flows Analytics] GustoAnalytics snippet included twice.');
  } else {
    GustoAnalyticsObj.invoked = true;
    GustoAnalyticsObj.methods = [
      'dataLayer',
      'dataLayerPii',
      'experimentEnrollment',
      'group',
      'gtmDataLayer',
      'identify',
      'inspectletTagSession',
      'page',
      'track',
      'trackDomEvent',
      'trackPii',
      'trackScrollEvent',
      'triggerRevenueForecastingEvent',
    ];
    GustoAnalyticsObj.factory = function (method: string) {
      return function (...args: unknown[]) {
        const methodCall: unknown[] = [method, ...args];
        GustoAnalyticsObj.push(methodCall);
        return GustoAnalyticsObj;
      };
    };
    for (let i = 0; i < GustoAnalyticsObj.methods.length; i += 1) {
      const method = GustoAnalyticsObj.methods[i];
      GustoAnalyticsObj[method] = GustoAnalyticsObj.factory(method);
    }
  }
}

// STEP 3: Load the analytics library
// Using dynamic import to ensure gustoAC is set first
// The library will auto-init and drain all queued method calls
void import('@gusto/gusto-analytics')
  .then(() => {
    console.info('[GWS-Flows Analytics] GustoAnalytics loaded in stateless mode');

    // Initialize automatic tracking after library is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initAnalytics();
        console.info('[GWS-Flows Analytics] Automatic tracking initialized');
      });
    } else {
      initAnalytics();
      console.info('[GWS-Flows Analytics] Automatic tracking initialized');
    }

    return undefined;
  })
  .catch((error: unknown) => {
    console.error('[GWS-Flows Analytics] Failed to load GustoAnalytics:', error);
  });
